import React, { Component } from "react";
import { connect } from "react-redux";
import { loggerEventName, loggerEventOutcome, loggerEventTypes, userMessages } from "../../../helpers/messages";
import { logger } from "../../../services/logger/logger-service";
import { validateAlphanumeric, validateEmployeeID, validateSNEmail, validateUserFields } from "../../../shared/ValidateUser";
import { getSNUserDetails, updateSNUserDetails } from "../../../services/java/java-services";
import MappedUserDetails from "./MappedUserDetails";

let startDate;
class MappedUserDetailsContainer extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			user: "",
			email: props?.email || "",
			editMode: false,
			showSuccessMsg: props.userCreated ? userMessages.mappedUserCreated : "",
			showEmailSuccessMsg: "",
			showErrorMsg: "",
			userSaveError: null,
			editObj: {
				firstName: "",
				lastName: "",
				email: "",
				role: "",
				employeeID: "",
			},
			errors: {
				firstName: "",
				lastName: "",
				email: "",
				employeeID: "",
				role: ""
			},
		};
	}

	componentDidMount() {
		startDate = new Date();
		window.addEventListener("beforeunload", this.timeSpentLogger);
		this.getMapUserDetails(this.props.email);
	}

	componentWillUnmount() {
		window.removeEventListener("beforeunload", this.timeSpentLogger);
		this.timeSpentLogger();
	}

	/**
	 * @description function to handle application logs for time spent on the page
	 * @param {*}
	 * @memberof MappedUserDetailsContainer
	 */
	timeSpentLogger(key) {
		// calculate the time since we loaded this page
		const timeSinceLoad = (new Date().getTime() - startDate.getTime()) / 1000;
		const loggerObj = {
			EventOutcome: loggerEventOutcome.success,
			EventType: loggerEventTypes.read,
			EventName: loggerEventName.mappedUserDetails,
			Content: {
				TimeSpent: timeSinceLoad,
			},
		};
		logger(loggerObj);
	}
	/**
	 * @description Function to get the user details
	 * @param email Unique email of the user to be fetched
	 * @memberof MappedUserDetailsContainer
	 */
	getMapUserDetails = async (email, rippleProp = false) => {
		this.setState({ loading: true });
		getSNUserDetails(email, (err, result) => {
			/* istanbul ignore next  */
			if (err) {
				this.setState({ loading: false, userError: err });
			} else {
				const user =
					result.data && result.data.user && result.data.user.length
						? result.data.user[0]
						: "";
				this.setState({
					loading: false,
					user: user,
					userError: null,
					...this.getEditObject(user),
				});
				if (rippleProp) {
					setTimeout(() => {
						this.props.mappedUserUpdated(true);
					}, 0);
				}
			}
		});
	};

	/** 
	 * @description function to update user in db
	 * @param {*}
	 * @memberof MappedUserDetailsContainer
	 */
	updateSNUserAPI = (params) => {
		const { email } = this.state
		this.setState({ showSuccessMsg: "", showErrorMsg: "" });
		updateSNUserDetails(params, (err, res) => {
			if (err) {
				const errMsg = err && err.message ? err.message : userMessages.saveError;
				this.setState({ loading: false, showErrorMsg: err });
				this.applicationLogger({ outcome: loggerEventOutcome.failure, message: `${errMsg} (For ${email})` });
				this.props.mappedUserUpdated(true);
			} else {
				this.setState({ showSuccessMsg: userMessages.saveUserMsg, showErrorMsg: "", loading: false, editMode: false, });
				this.getMapUserDetails(res?.email || email, true);
			}
		});
	};

	/**
	 * @description Function to handle state when edit is cancelled
	 * @param user User Details
	 * @memberof MappedUserDetailsContainer
	 */
	getEditObject(user) {
		const editObj = {
			firstName: user && user.firstName ? user.firstName : "",
			lastName: user && user.lastName ? user.lastName : "",
			email: user && user.email ? user.email : "",
			employeeID: user && user.employeeid ? user.employeeid : "",
			role: user && user.daprole ? user.daprole : "",
		};
		return {
			editObj: editObj,
			errors: {
				firstName: "",
				lastName: "",
				email: "",
			},
		};
	}

	/**
	 * @description Function to enable/disable edit mode
	 * @memberof MappedUserDetailsContainer
	 */
	toggleEditMode = () => {
		this.setState(prevState => {
			return {
				...this.getEditObject(prevState.user),
				editMode: !prevState.editMode,
				showSuccessMsg: ''
			};
		});
	}

	/**
	 * @description Function to handle input changes
	 * @param  value Value of the input
	 * @param  field Input field name
	 * @memberof MappedUserDetailsContainer
	 */
	handleInputChange = (field, value) => {
		this.setState({
			showSuccessMsg: "",
			showErrorMsg: "",
			userSaveError: null,
		});
		var { errors } = this.state;
		var errorMessage = !value ? userMessages[field] : "";
		if ((field === "firstName" || field === "lastName") && value) {
			const surgeonNameCheck = validateAlphanumeric(value);
			errorMessage = surgeonNameCheck.error;
		}

		if (field === "employeeID") {
			if (value) {
				const employeeID = validateEmployeeID(value);
				errorMessage = employeeID.error;
			} else {
				errorMessage = "";
			}
		}

		this.setState((prevState) => {
			return {
				[field]: value,
				editObj: {
					...prevState.editObj,
					[field]: value,
				},
				errors: {
					...errors,
					[field]: errorMessage,
				},
			};
		});
	};

	/**
	 * @description Function to handle blur event in  form
	 * @memberof MappedUserDetailsContainer
	 */
	handleBlur = (field, value) => {
		var { errors } = this.state;
		var errorMessage = !value ? userMessages[field] : "";
		if (field === "email" && value) {
			const emailCheck = validateSNEmail(value);
			errorMessage = emailCheck.error;
		}
		this.setState((prevState) => {
			return {
				[field]: value,
				editObj: {
					...prevState.editObj,
					[field]: value,
				},
				errors: {
					...errors,
					[field]: errorMessage,
				},
			};
		});
	};

	/**
	 * @description Function submit form by hitting enter key
	 *  @param e Keypress event
	 *  @memberof MappedUserDetailsContainer
	 */
	onEnterPress = (e) => {
		if (e.which === 13) {
			e.preventDefault();
			this.saveUser();
		}
	};

	/**
	 * @description Function to validate fields at the time of submit
	 * @returns Boolean value if there are any errors
	 * @memberof MappedUserDetailsContainer
	 */
	validateFields = () => {
		const { editObj } = this.state;
		const userFields = ["firstName", "lastName", "email", "role"];
		let { errorFlag, errors } = validateUserFields(
			{ editObj: editObj, errors: this.state.errors },
			userFields
		);
		if (!errorFlag && editObj.firstName) {
			let firstName = validateAlphanumeric(editObj.firstName);
			errorFlag = firstName.errorFlag;
			errors["firstName"] = firstName.error;
		}
		if (!errorFlag && editObj.lastName) {
			let lastName = validateAlphanumeric(editObj.lastName);
			errorFlag = lastName.errorFlag;
			errors["lastName"] = lastName.error;
		}
		if (!errorFlag && editObj.email) {
			let email = validateSNEmail(editObj.email);
			errorFlag = email.errorFlag;
			errors["email"] = email.error;
		}
		if (!errorFlag && editObj.employeeID) {
			let employeeID = validateEmployeeID(editObj.employeeID);
			errorFlag = employeeID.errorFlag;
			errors["employeeID"] = employeeID.error;
		}

		if (errorFlag) {
			this.setState((prevState) => {
				return {
					errors: {
						...prevState.errors,
						...errors,
					},
				};
			});
		}

		return errorFlag;
	};

	/**
	 * @description function to validate and save user details
	 * @param {*}
	 * @memberof MappedUserDetailsContainer
	 */
	saveUser = () => {
		this.setState({ showSuccessMsg: "", showErrorMsg: "" });
		const formHasErrors = this.validateFields();

		if (!formHasErrors) {
			const { editObj, user } = this.state;
			this.setState({ loading: true });
			let updateAttributes = {};

			if (editObj.firstName !== user.firstName) {
				updateAttributes["firstname"] = editObj.firstName;
			}
			if (editObj.lastName !== user.lastName) {
				updateAttributes["lastname"] = editObj.lastName;
			}

			// Always send current email in "email"
			updateAttributes["email"] = user.email;

			// If email is changed, send it as updatedEmail
			if (editObj.email !== user.email) {
				updateAttributes["updatedEmail"] = editObj.email;
			}

			if (editObj.employeeID !== user.employeeid) {
				updateAttributes["employeeId"] = editObj.employeeID;
			}

			if (editObj.role !== user.daprole) {
				updateAttributes["dapRole"] = editObj.role;
			}

			this.updateSNUserAPI(updateAttributes);
		}
	};

	/**
	 * @description function to log user creation event
	 * @memberof MappedUserDetailsContainer
	 */
	applicationLogger = (params) => {
		const loggerObj = {
			EventOutcome: params.outcome,
			EventType: loggerEventTypes.update,
			EventName: loggerEventName.mappedUserUpdate,
			Content: {
				Data: params.message,
			},
		};
		logger(loggerObj);
	};

	render() {
		return (
			<MappedUserDetails
				loading={this.state.loading}
				editObj={this.state.editObj}
				userSaveError={this.state.userSaveError}
				handleInputChange={this.handleInputChange}
				handleBlur={this.handleBlur}
				onEnterPress={this.onEnterPress}
				showSuccessMsg={this.state.showSuccessMsg}
				showEmailSuccessMsg={this.state.showEmailSuccessMsg}
				showErrorMsg={this.state.showErrorMsg}
				formErrors={this.state.errors}
				saveUser={this.saveUser}
				handleCancel={this.props.handleCancel}
				editMode={this.state.editMode}
				toggleEditMode={this.toggleEditMode}
				user={this.state.user}
				userCreated={this.props.userCreated}
			/>
		);
	}
}

function mapStateToProps(state) {
	return {
		state,
	};
}

export default connect(mapStateToProps)(MappedUserDetailsContainer);
